import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { IMPERSONATE_USER_ID } from '@booking-booster-client/helpers';
import {
  BalanceStoreActions,
  PaymentsStoreActions,
  PreferencesStoreActions,
  SearchEnginesStoreActions,
  supportedLanguagesActionGroup,
  UserSessionStoreActions,
  UserSessionStoreSelectors,
} from '@booking-booster-client/root-store';
import { LocalStorageService } from '@booking-booster-client/services';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';
import { combineLatest, filter, Subscription, take } from 'rxjs';
import { AuthService } from './services/auth.service';
import { TourStepsRegistryService } from './services/tour-steps-registry.service';

@Component({
  selector: 'bb-root',
  templateUrl: './app.component.html',
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private store = inject(Store);

  private localStorageService = inject(LocalStorageService);

  impersonateUserId$ = this.store.pipe(
    select(UserSessionStoreSelectors.selectImpersonateUserId),
  );

  private subs = new Subscription();

  selectedOrganization$ = this.store.pipe(
    select(UserSessionStoreSelectors.selectSelectedOrganizationId),
  );

  constructor(
    private config: PrimeNG,
    private translateService: TranslateService,
    private authService: AuthService,
    public tourStepsRegistryService: TourStepsRegistryService,
  ) {}

  ngOnInit(): void {
    const impersonateUserId =
      this.localStorageService.getItem(IMPERSONATE_USER_ID);

    if (impersonateUserId) {
      this.store.dispatch(
        UserSessionStoreActions.impersonateRequest({
          userId: impersonateUserId,
        }),
      );

      this.impersonateUserId$
        .pipe(
          filter((userId) => !!userId),
          take(1),
        )
        .subscribe(() => {
          this.mainSetup();
        });
    } else {
      this.mainSetup();
    }
  }

  mainSetup() {
    this.translateService.get('primeng').subscribe((res) => {
      this.config.setTranslation(res);
    });

    this.authService.isInitialized$
      .pipe(
        filter((initialized) => initialized),
        take(1),
      )
      .subscribe(() => {
        if (this.authService.hasValidAccessToken()) {
          this.loadBasicInfo();
        }

        this.authService.events
          .pipe(
            filter(
              (event) =>
                event.type === 'silent_refresh_timeout' ||
                event.type === 'invalid_nonce_in_state' ||
                event.type === 'token_error',
            ),
          )
          .subscribe(() => {
            this.authService.initLoginFlow();
          });
      });

    this.subs.add(
      this.selectedOrganization$.subscribe((organization) => {
        if (organization) {
          this.loadOrganizationInfo();
        }
      }),
    );
  }

  loadBasicInfo() {
    this.store.dispatch(UserSessionStoreActions.loadRequest({}));
    this.store.dispatch(UserSessionStoreActions.loadProfileRequest());

    this.store.dispatch(supportedLanguagesActionGroup.loadRequest());

    combineLatest([
      this.store.pipe(select(UserSessionStoreSelectors.selectUser)),
      this.store.pipe(select(UserSessionStoreSelectors.selectError)),
    ])
      .pipe(
        filter(([user, error]) => !!user || !!error),
        take(1),
      )
      .subscribe(([user]) => {
        if (user) {
          this.store.dispatch(PaymentsStoreActions.loadPaymentMethodsRequest());
        }
      });
  }

  loadOrganizationInfo() {
    this.store.dispatch(UserSessionStoreActions.loadPermissionsRequest());
    this.store.dispatch(SearchEnginesStoreActions.loadRequest());
    this.store.dispatch(BalanceStoreActions.loadBalanceRequest());
    this.store.dispatch(SearchEnginesStoreActions.loadRequest());
    this.store.dispatch(PreferencesStoreActions.loadPreferencesRequest({}));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
